import {validInvoiceNumberTemplate} from "../utils/workflow-invoice-number";
import {inject, observer} from "mobx-react";
import React, {useEffect, useMemo, useRef, useState} from "react";
import toast from "react-hot-toast";
import Modal from "../components/modal";
import WorkflowBranchEdit from "../components/workflow-branch-edit";
import FilterInputField from "../components/filter-input-field";
import PaginationControls from "../components/pagination-controls";
import Checkbox from "../components/checkbox";
import Switch from "react-switch";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronCircleLeft, faExclamationTriangle, faPencilAlt, faPlus} from "@fortawesome/free-solid-svg-icons";
import {fetch} from "../utils/api-wrapper";
import WorkflowEditStepModal from "../components/workflow-edit-step-modal/workflow-edit-step-modal";
import WorkflowTriggerEdit from "../workflow-trigger-edit";
import HoverInfoCard from "../components/hover-card";
import { IMPUTATION_CATEGORY_SORTING, IMPUTATION_FETCH_PAGE_SIZE, imputationRowHasParentId } from "../utils/expenses-imputations-config-helpers";
import { findStaleCategoryConditions } from "../utils/workflow-stale-category";
import { loadXpensCategories } from "../utils/xpens-categories";

const STATUS_TRANSITION_STATES = [
    "any",
    "all",
    "completed",
    "invalid",
    "need_attention",
    "pending",
    "processing",
    "validated",
    "approved",
    "need_approval",
    "denied",
    "finalized",
    "deleted",
    "submitted",
];

const TRIGGER_CONFIGS = {
    invoice_status_transitions: {
        labelKey: "rule.trigger_invoice_status_transitions",
        defaultInitial: "any",
        defaultFinal: "completed",
        states: STATUS_TRANSITION_STATES,
    },
    expense_status_transitions: {
        labelKey: "rule.trigger_expense_status_transitions",
        defaultInitial: "any",
        defaultFinal: "completed",
        states: STATUS_TRANSITION_STATES,
    },
    is_billable_transition: {
        labelKey: "rule.trigger_is_billable_transition",
        defaultInitial: "false",
        defaultFinal: "true",
        states: ["false", "true"],
    },
    expense_account_submitted: {
        labelKey: "rule.trigger_expense_account_submitted",
        states: [],
    },
};

const DEFAULT_TRIGGER_TYPE = "invoice_status_transitions";

const triggerHasStates = (config) => Array.isArray(config?.states) && config.states.length > 0;

const sanitizeTrigger = (trigger) => {
    const rawTrigger = typeof trigger === "object" && trigger !== null ? { ...trigger } : {};
    const hasValidType =
        rawTrigger.type && Object.prototype.hasOwnProperty.call(TRIGGER_CONFIGS, rawTrigger.type);
    const type = hasValidType ? rawTrigger.type : DEFAULT_TRIGGER_TYPE;
    const config = TRIGGER_CONFIGS[type];
    const hasStates = triggerHasStates(config);
    if (!hasStates) {
        const { initial_state, final_state, ...rest } = rawTrigger;
        return {
            ...rest,
            type,
        };
    }
    const initial_state = config.states.includes(rawTrigger.initial_state)
        ? rawTrigger.initial_state
        : config.defaultInitial;
    const final_state = config.states.includes(rawTrigger.final_state)
        ? rawTrigger.final_state
        : config.defaultFinal;
    return {
        ...rawTrigger,
        type,
        initial_state,
        final_state,
    };
};

const prepareWorkflowForPersistence = (workflow) => {
    if (!workflow) return workflow;
    return {
        ...workflow,
        trigger: sanitizeTrigger(workflow.trigger),
    };
};

const isPlainIntegerString = (value) => typeof value === "string" && /^\d+$/.test(value.trim());

const isValidGlBetweenValue = (value) => {
    if (typeof value !== "object" || value === null) return false;
    if (!isPlainIntegerString(value.min) || !isPlainIntegerString(value.max)) return false;
    return Number(value.min) <= Number(value.max);
};

// Main Workflow component
const Workflow = ({ data }) => {
    // STATE VARIABLES
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [workflows, setWorkflows] = useState([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [expenseRates, setExpenseRates] = useState({});
    // Live expense-catalog imputations: stale-check uses Level 1 roots;
    // display cards use the same list to resolve client_category labels.
    const [clientImputations, setClientImputations] = useState([]);

    // For editing a specific step and panel (trigger, conditions, or actions)
    const [selectedStep, setSelectedStep] = useState(null);
    // Branch whose precondition is being edited, and the draft the modal works on
    // (it edits conditions, not a step).
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [branchConditionsDraft, setBranchConditionsDraft] = useState(null);

    // Modal controls
    const [showDiscardChangesModal, setShowDiscardChangesModal] = useState(false);
    const [showWorkflowDetails, setShowWorkflowDetails] = useState(false);
    const [showDeleteWorkflowsModal, setShowDeleteWorkflowsModal] = useState(false);

    // Delete selection state
    const [workflowsDeleteSelection, setWorkflowsDeleteSelection] = useState([]);

    // Pagination and filter state
    const [pageSize, setPageSize] = useState(20);
    const [filterValue, setFilterValue] = useState("");
    const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
    const [totalRowCount, setTotalRowCount] = useState(0);
    const [page, setPage] = useState(1);

    const branchButtonRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        fetchWorkflows();
        fetchExpenseRates();
        fetchClientImputations();
        loadXpensCategories(data, (message) => toast.error(message));
    }, []);

    useEffect(() => {
        setPage(1);
        fetchWorkflows();
    }, [pageSize, sorting, filterValue]);

    useEffect(() => {
        fetchWorkflows();
    }, [page]);

    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800" : "0 !important",
        }),
    };

    const translate = (key, fallback) => {
        if (!data?.t?.read) return fallback ?? key;
        const translated = data.t.read(key);
        if (translated === undefined || translated === null || translated === key) {
            return fallback ?? key;
        }
        return translated;
    };

    const liveRootImputations = useMemo(
        () => clientImputations.filter((row) => !imputationRowHasParentId(row)),
        [clientImputations]
    );

    const staleCategoryWarning = (workflow) => {
        // PR review: this screen has no per-category row to hang a generic icon
        // on the way the categories screen does — the message itself must name
        // the stale category so the admin knows what to fix.
        const staleCategories = findStaleCategoryConditions(workflow, {
            xpensCategories: data.xpensCategories,
            rootImputations: liveRootImputations,
        });
        if (staleCategories.length === 0) return null;
        return (
            <HoverInfoCard
                info={<FontAwesomeIcon icon={faExclamationTriangle} color="orange" className="ml-2" />}
                additionalInfo={
                    <div className="alert alert-danger m-0" style={{ minWidth: "220px" }}>
                        {data.t.read("rule.category_no_longer_exists_warning")} ({staleCategories.join(", ")})
                    </div>
                }
                custom={true}
                position="pos-top-right"
            />
        );
    };

    const getSaveErrorMessage = (error, fallbackKey) => {
        const responseMessage = error?.response?.data?.message;
        if (responseMessage) {
            const translated = translate(`rule.${responseMessage}`, responseMessage);
            return translated;
        }
        return data.t.read(fallbackKey);
    };

    // HELPERS: Assign unique step IDs and branch IDs recursively.
    const assignUniqueStepIds = (workflow) => {
        let counter = 0;
        function processBranches(branches) {
            if (!branches || !branches.length) return;
            branches.forEach((branch) => {
                if (Array.isArray(branch.steps)) {
                    branch.steps.forEach((step) => {
                        counter++;
                        step.id = counter;
                        // Process nested branches inside a step if present
                        if (Array.isArray(step.branches) && step.branches.length > 0) {
                            processBranches(step.branches);
                        }
                    });
                }
            });
        }
        processBranches(workflow.branches);
        return workflow;
    };

    function assignUniqueBranchIds(workflow) {
        let counter = 0;
        function processBranches(branches) {
            if (!branches || !branches.length) return;
            branches.forEach((branch) => {
                counter++;
                branch.id = counter;
                if (Array.isArray(branch.steps)) {
                    branch.steps.forEach((step) => {
                        if (Array.isArray(step.branches) && step.branches.length > 0) {
                            processBranches(step.branches);
                        }
                    });
                }
            });
        }
        processBranches(workflow.branches);
        return workflow;
    }

    // SCROLL HELPER: Center the container on the branch button
    const centerContainer = () => {
        if (containerRef.current && branchButtonRef.current) {
            const containerRect = containerRef.current.getBoundingClientRect();
            const buttonRect = branchButtonRef.current.getBoundingClientRect();
            const offsetLeft = buttonRect.left - containerRect.left;
            const scrollAmount =
                containerRef.current.scrollLeft +
                offsetLeft +
                buttonRect.width * 3 -
                containerRect.width / 2;
            containerRef.current.scrollTo({
                left: scrollAmount,
                behavior: "smooth",
            });
        }
    };

    // API CALL: Fetch workflows with current filters and pagination.
    const fetchWorkflows = () => {
        const start = (page - 1) * pageSize;
        const filters = [];
        if (filterValue && filterValue.trim() !== "") {
            filters.push({ id: "search", value: filterValue.trim() });
        }
        const searchData = `?filters=${encodeURIComponent(
            JSON.stringify(filters ?? [])
        )}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}&start=${encodeURIComponent(
            start
        )}&page_size=${pageSize}`;
        fetch(`/workflows/${searchData}`, "GET", {},
            (response) => {
                let fetchedWorkflows = response.data.workflows
                fetchedWorkflows.sort((a, b) => {
                    return (a.priority ?? 1) - (b.priority ?? 1)
                });

                // Process each workflow by assigning unique IDs
                fetchedWorkflows = fetchedWorkflows.map((w) => {
                    w.saved = true;
                    return assignUniqueStepIds(assignUniqueBranchIds(w));
                });



                setWorkflows(fetchedWorkflows);
                setTotalRowCount(response.data.meta.totalRowCount);
                setLoading(false);
            },
            (error) => {
                setErrorMessage(data.t.read("failed_to_fetch"));
            }
        );
    };

    const fetchExpenseRates = () => {
        fetch("/expense_rates/", "GET", {},
            response => setExpenseRates(response.data?.rates || {}),
            () => {}
        );
    };

    // XPEN-3192 / D4: same pattern as expenses_rules.js's fetchClientImputations —
    // scoped to the expense catalog only, paginated so a large catalog is not
    // silently truncated at the API's default page size.
    const fetchClientImputations = () => {
        const filters = [{ id: "imputation_catalog", value: "expense" }];
        const all = [];
        const fetchPage = (start) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${encodeURIComponent(JSON.stringify(IMPUTATION_CATEGORY_SORTING))}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
            fetch(`/imputations/${searchData}`, "GET", {}, (response) => {
                const rows = response?.data?.imputations ?? [];
                const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
                all.push(...rows);
                if (rows.length > 0 && all.length < totalRowCount) {
                    fetchPage(start + rows.length);
                } else {
                    setClientImputations(all);
                }
            }, () => {
                // PR review: without this, a failed load leaves clientImputations
                // at [] and the stale-category check's fail-safe silently hides
                // every warning on this screen for the rest of the session.
                toast.error(data.t.read("failed_to_fetch"));
            });
        };
        fetchPage(0);
    };

    // API CALL: Create a new workflow
    const createWorkflow = () => {
        const workflowToPersist = prepareWorkflowForPersistence(selectedWorkflow);
        const formData = new FormData();
        formData.append("workflow", JSON.stringify(workflowToPersist));
        setSaving(true);
        fetch("/workflows/", "POST", formData,
            (response) => {
                const workflowId = response?.data?.workflow_id || response?.data?.office_id;
                setSelectedWorkflow({
                    ...selectedWorkflow,
                    _id: workflowId ? { $oid: workflowId } : selectedWorkflow?._id,
                    saved: true,
                });
                toast.success(data.t.read("workflow_created_successfully"));
                setSaving(false);
                fetchWorkflows();
            },
            (error) => {
                toast.error(getSaveErrorMessage(error, "failed_to_create_workflow"));
                setLoading(false);
                setSaving(false);
            }
        );
    };

    // API CALL: Update an existing workflow
    const updateWorkflow = (workflow = { ...selectedWorkflow }) => {
        const workflowToSave = prepareWorkflowForPersistence(workflow);
        delete workflowToSave._id;
        const formData = new FormData();
        formData.append("workflow", JSON.stringify(workflowToSave));
        const workflowId = workflow._id?.$oid;
        setSaving(true);
        fetch(`/workflows/${workflowId}`, "PUT", formData,
            () => {
                fetchWorkflows();
                setSelectedWorkflow(selectedWorkflow !== null ? {...selectedWorkflow, saved: true} : null);
                toast.success(data.t.read("workflow_updated_successfully"));
                setSaving(false);
            },
            (error) => {
                toast.error(getSaveErrorMessage(error, "failed_to_update_workflow"));
                setLoading(false);
                setSaving(false);
            }
        );
    };

    // SAVE HANDLER: Validate before saving workflow
    const handleSave = () => {
        if (validateWorkflow(selectedWorkflow) === false) {
            toast.error(data.t.read("rule.some_fields_empty_warning"));
            return;
        }
        if (selectedWorkflow._id) {
            updateWorkflow();
        } else {
            createWorkflow();
        }
    };

    // RECURSIVE VALIDATION FUNCTIONS

    // Validate workflow-level fields and then recursively check branches.
    function validateWorkflow(workflow) {
        if (!workflow.name || workflow.name.trim() === "") return false;
        // description is optional — workflows saved without one (e.g. created via the API) must stay editable
        const trigger = workflow.trigger;
        if (!trigger) return false;
        if (!trigger.type || trigger.type.trim() === "") return false;
        const triggerConfig = TRIGGER_CONFIGS[trigger.type];
        if (!triggerConfig) return false;
        const hasStates = triggerHasStates(triggerConfig);
        if (hasStates) {
            if (!trigger.initial_state || trigger.initial_state.trim() === "") return false;
            if (!trigger.final_state || trigger.final_state.trim() === "") return false;
            if (!triggerConfig.states.includes(trigger.initial_state)) return false;
            if (!triggerConfig.states.includes(trigger.final_state)) return false;
        }
        if (!workflow.branches || !Array.isArray(workflow.branches) || workflow.branches.length === 0) return false;
        for (let branch of workflow.branches) {
            if (!validateBranch(branch)) return false;
        }
        return true;
    }

    // Validate each branch recursively.
    function validateBranch(branch) {
        if (!branch.steps || !Array.isArray(branch.steps) || branch.steps.length === 0) return false;
        for (let condition of branch.precondition?.conditions || []) {
            if (!validateCondition(condition)) return false;
        }
        for (let step of branch.steps) {
            // If step contains nested branches, validate them recursively.
            if (step.branches && Array.isArray(step.branches) && step.branches.length > 0) {
                for (let nestedBranch of step.branches) {
                    if (!validateBranch(nestedBranch)) return false;
                }
            } else if (!validateStep(step)) {
                return false;
            }
        }
        return true;
    }

    // Validate a step: it must have at least one condition and one action.
    function validateStep(step) {
        if (step.condition_logic !== "always" && (!Array.isArray(step.conditions) || step.conditions.length === 0)) return false;
        if (!step.actions || !Array.isArray(step.actions) || step.actions.length === 0) return false;
        for (let condition of step.condition_logic === "always" ? [] : step.conditions) {
            if (!validateCondition(condition)) return false;
        }
        // A side in use follows the same rule as the primary one: it needs a gate and
        // something to run. Without this, alternate actions with no alternate
        // conditions save fine and are inert — the API skips an ungated alternate side.
        const alternateConditions = step.alternate_conditions || [];
        const alternateActions = step.alternate_actions || [];
        const alternateInUse = alternateConditions.length > 0 || alternateActions.length > 0;
        if (alternateInUse) {
            if (step.alternate_condition_logic !== "always" && alternateConditions.length === 0) return false;
            if (alternateActions.length === 0) return false;
        }
        // Skipped under "always" like the primary list: the modal hides it there, so an
        // incomplete hidden condition would block saving with nothing visible to fix.
        for (let condition of step.alternate_condition_logic === "always" ? [] : alternateConditions) {
            if (!validateCondition(condition)) return false;
        }
        for (let action of [...step.actions, ...alternateActions]) {
            if (!validateAction(action)) return false;
        }
        return true;
    }

    // Validate a condition – requires nonempty type, operator (if needed), and value.
    function validateCondition(condition) {
        if (!condition.type || condition.type.trim() === "") return false;
        // For some types, operator is required.
        if (
            (condition.type === "item_description" ||
                condition.type === "item_cost_type" ||
                condition.type === "item_gl" ||
                condition.type === "item_gl_class" ||
                condition.type === "supplier_number" ||
                condition.type === "tax_code" ||
                condition.type === "item_tax_code") &&
            (!condition.operator || condition.operator.trim() === "")
        ) {
            return false;
        }
        if (condition.type === "user_has_role"){
            if (
                condition.role === undefined ||
                condition.role === null ||
                condition.role.toString().trim() === ""
            ) {
                return false;
            }        }
        else if (condition.type === "item_gl" && condition.operator === "between") {
            return isValidGlBetweenValue(condition.value);
        }
        else if (condition.type === "invoice_number_missing") {
            return true
        }
        else if (
            condition.value === undefined ||
            condition.value === null ||
            condition.value.toString().trim() === ""
        ) {
            return false;
        }
        return true;
    }

    // Validate an action – requires nonempty type and value.
    function validateAction(action) {
        if (!action.type || action.type.trim() === "") return false;
        const isGLSectionWorkflowVariable = action.field === "GLSection" &&
            action.value?.type === "workflow_variable" &&
            action.value?.value === "first_4_digits_of_gl_natural";
        if (action.type === "change_item_description") {
            const hasValue = action.value !== undefined && action.value !== null && action.value.toString().trim() !== "";
            const hasVariable = Boolean(action.include_supplier_name) || Boolean(action.include_invoice_number);
            return hasValue || hasVariable;
        }
        if (["change_item_detail", "change_invoice_default_field_value"].includes(action.type) && isGLSectionWorkflowVariable) {
            return true;
        }
        if (["request_department_approval", "request_line_item_department_approval"].includes(action.type)) {
            return Boolean(action?.value?.department) && Boolean(action?.value?.function);
        }
        if (action.type === "request_invoice_approval") {
            const source = action?.value?.approver_source;
            if (!source) return false;
            if (source === "manual_user") {
                return Boolean(action?.value?.user_id);
            }
            if (source === "department") {
                return Boolean(action?.value?.department) && Boolean(action?.value?.function);
            }
            return ["invoice_user", "gl_responsible", "matter_responsible", "matter_practice_group_timekeeper"].includes(source);
        }
        if (action.type === "request_invoice_any_of_approval") {
            if (!Array.isArray(action.value) || action.value.length === 0) return false;
            return action.value.every((spec) => {
                const src = spec?.approver_source;
                if (!src) return false;
                if (src === "manual_user") return Boolean(spec?.user_id);
                if (src === "department") return Boolean(spec?.department) && Boolean(spec?.function);
                return ["invoice_user", "gl_responsible", "matter_responsible", "matter_practice_group_timekeeper"].includes(src);
            });
        }
        if (action.type === "request_line_item_approval" && typeof action.value === "object" && action.value !== null) {
            const source = action?.value?.approver_source;
            if (!source) return false;
            if (source === "manual_user") {
                return Boolean(action?.value?.user_id);
            }
            if (source === "department") {
                return Boolean(action?.value?.department) && Boolean(action?.value?.function);
            }
            return ["gl_responsible", "matter_responsible", "matter_practice_group_timekeeper", "category_responsible", "subcategory_responsible", "subcategory_approvers"].includes(source);
        }
        if (action.type === "change_due_date") {
            if (!action.value || action.value.toString().trim() === "") return false;
            if (action.value === "on_day_next_month") {
                const day = Number(action.day);
                return Number.isInteger(day) && day >= 1 && day <= 31;
            }
            return true;
        }
        if (action.type === "split_mileage_by_secondary_rate") {
            const secondaryRate = Number(action.value?.secondary_rate);
            const travelTypeId = action.value?.travel_type_id;
            const selectedTT = travelTypeId
                ? (expenseRates?.mileage_travel_types ?? []).find(t => t.id === travelTypeId)
                : null;
            const standardRate = selectedTT ? Number(selectedTT.rate) : Number(expenseRates?.mileage_details?.rate);
            // XPEN-3428: an open_amount travel type has no rate, so standardRate
            // is NaN and the finiteness check below would otherwise wave this
            // step through. Reject on the mode directly, not on the missing
            // rate, so this cannot be "simplified" back into that comparison.
            if (selectedTT?.travel_mode === "open_amount") return false;
            return Boolean(travelTypeId) &&
                Number.isFinite(secondaryRate) &&
                secondaryRate > 0 &&
                (!Number.isFinite(standardRate) || secondaryRate < standardRate) &&
                Boolean(action.value?.billable_cost_type);
        }
        if (action.type === "split_tip_into_separate_item") {
            return Boolean(action.value?.cost_type);
        }
        if (action.type === "set_invoice_number") return validInvoiceNumberTemplate(action.value);
        if (action.type === "rename_invoice_file") {
            const pieces = Array.isArray(action.value?.pieces) ? action.value.pieces : [];
            return pieces.some(piece => Boolean(piece));
        }
        if (!["only_keep_total", "achat_and_payment", "request_matter_responsible_approval", "request_gl_responsible_approval", "request_line_item_matter_responsible_approval", "request_line_item_gl_responsible_approval", "request_line_item_category_responsible_approval", "request_line_item_subcategory_responsible_approval", "delay_approval", "use_invoice_date_as_invoice_number", "apply_gl_description_to_items", "delete_line_item", "change_reference_to_matter_number", "request_invoice_user_approval"].includes(action.type) &&
            (action.value === undefined ||
            action.value === null ||
            action.value.toString().trim() === "")
        ) {
            return false;
        }
        return true;
    }

    // API CALL: Delete selected workflows
    const deleteWorkflows = (deletedSelection = workflowsDeleteSelection) => {
        fetch("/workflows/", "DELETE", { workflow_ids: deletedSelection },
            () => {
                fetchWorkflows();
                setWorkflowsDeleteSelection([]);
                setShowDeleteWorkflowsModal(false);
                setSelectedWorkflow(null)
            },
            (error) => {
                setErrorMessage(data.t.read("failed_to_delete"));
                setLoading(false);
                setShowDeleteWorkflowsModal(false);
            }
        );
    };

    // Cancel delete modal
    const cancelDeleteWorkflowModal = () => {
        setShowDeleteWorkflowsModal(false);
        setWorkflowsDeleteSelection([]);
    };

    // Helpers for checkbox selection in the workflow list
    const isAllWorkflowsSelected = () => {
        if (!workflows || !workflows.length) return false;
        return workflows.every((w) => workflowsDeleteSelection.includes(w._id?.$oid));
    };

    const isWorkflowSelected = (workflow) => {
        return workflowsDeleteSelection.includes(workflow._id?.$oid);
    };

    const toggleAllWorkflowsSelected = () => {
        setWorkflowsDeleteSelection(
            isAllWorkflowsSelected()
                ? workflowsDeleteSelection.filter(
                    (id) => !workflows.some((w) => w._id?.$oid === id)
                )
                : [
                    ...workflowsDeleteSelection,
                    ...workflows
                        .map((w) => w._id?.$oid)
                        .filter((id) => !workflowsDeleteSelection.includes(id)),
                ]
        );
    };

    // Create a new workflow (with default values)
    const newWorkflow = () => {
        const defaultTriggerConfig = TRIGGER_CONFIGS[DEFAULT_TRIGGER_TYPE];
        let new_workflow = {
            description: "New workflow",
            name: "Untitled Workflow",
            date_created: Date.now(),
            date_updated: Date.now(),
            active: true,
            priority: 1,
            saved: false,
            trigger: sanitizeTrigger({
                type: DEFAULT_TRIGGER_TYPE,
                initial_state: defaultTriggerConfig.defaultInitial,
                final_state: defaultTriggerConfig.defaultFinal,
            }),
            branches: [
                {
                    id: "",
                    steps: [
                        {
                            "name":"",
                            "active": true,
                            type: "step",
                            condition_logic: "all",
                            conditions: [
                                {
                                    id: 1,
                                    type: "amount_greater",
                                    operator: "contain",
                                    value: 0,
                                },
                            ],
                            actions: [
                                {
                                    id: 1,
                                    type: "send_email",
                                    value: "",
                                },
                            ],
                        },
                    ],
                },
            ],
        };
        setSelectedWorkflow({ ...new_workflow });
        // Optionally show details panel
        setShowWorkflowDetails(true);
        setSelectedStep(null);
    };

    // Update the workflow trigger object
    const updateTrigger = (property, value) => {
        setSelectedWorkflow((previousWorkflow) => {
            if (!previousWorkflow) return previousWorkflow;
            const previousTrigger = sanitizeTrigger(previousWorkflow.trigger);
            let nextTrigger;

            if (property === "type") {
                nextTrigger = sanitizeTrigger({ type: value });
            } else {
                nextTrigger = sanitizeTrigger({
                    ...previousTrigger,
                    [property]: value,
                });
            }

            if (
                nextTrigger.type === previousTrigger.type &&
                nextTrigger.initial_state === previousTrigger.initial_state &&
                nextTrigger.final_state === previousTrigger.final_state
            ) {
                return previousWorkflow;
            }

            return {
                ...previousWorkflow,
                trigger: nextTrigger,
                saved: false,
            };
        });
    };

    const openWorkflow = (workflow) => {
        if (!workflow) {
            setSelectedWorkflow(null);
            setSelectedStep(null);
            return;
        }
        const workflowCopy = JSON.parse(JSON.stringify(workflow));
        workflowCopy.trigger = sanitizeTrigger(workflowCopy.trigger);
        setSelectedStep(null);
        setSelectedWorkflow(workflowCopy);
    };

    // RECURSIVE FUNCTIONS FOR REMOVING AND UPDATING STEPS AND BRANCHES
    const removeStepFromBranch = (branch, step_index) => {
        let workflow = { ...selectedWorkflow };
        let targetBranchId = branch.id;

        // Recursively search for the branch and remove the step at step_index
        function searchAndRemove(branches) {
            if (!branches) return false;
            for (let branch of branches) {
                if (branch.id === targetBranchId) {
                    branch.steps.splice(step_index, 1);
                    return true;
                }
                if (branch.steps && branch.steps.length > 0) {
                    for (let step of branch.steps) {
                        if (step.branches && step.branches.length > 0) {
                            if (searchAndRemove(step.branches)) return true;
                        }
                    }
                }
            }
            return false;
        }

        // Cleanup any empty nested branches
        function cleanUpBranches(branches) {
            if (!branches) return [];
            branches.forEach((branch) => {
                if (branch.steps && branch.steps.length > 0) {
                    branch.steps.forEach((step) => {
                        if (step.type === "branch") {
                            step.branches = cleanUpBranches(step.branches);
                        }
                    });
                    branch.steps = branch.steps.filter((step) => {
                        if (step.type === "branch" && step.branches.length === 0) return false;
                        return true;
                    });
                }
            });
            return branches.filter((branch) => branch.steps && branch.steps.length > 0);
        }

        searchAndRemove(workflow.branches);
        workflow.branches = cleanUpBranches(workflow.branches);
        workflow = assignUniqueBranchIds(assignUniqueStepIds(workflow));
        workflow.saved = false;
        setSelectedWorkflow(workflow);
    };

    // Remove branch recursively (similar structure as removeStepFromBranch)
    const removeBranch = (branch, index) => {
        let workflow = { ...selectedWorkflow };
        let targetBranchId = branch.id;

        function searchAndRemove(branches) {
            if (!branches) return false;
            for (let branch of branches) {
                if (branch.id === targetBranchId) {
                    branch.steps.forEach((step) => {
                        if (step.type === "branch") {
                            step.branches?.splice(index, 1);
                        }
                    });
                    return true;
                }
                if (branch.steps && branch.steps.length > 0) {
                    for (let step of branch.steps) {
                        if (step.branches && step.branches.length > 0) {
                            if (searchAndRemove(step.branches)) return true;
                        }
                    }
                }
            }
            return false;
        }
        function cleanUpBranches(branches) {
            if (!branches) return [];
            branches.forEach((branch) => {
                if (branch.steps && branch.steps.length > 0) {
                    branch.steps.forEach((step) => {
                        if (step.type === "branch") {
                            step.branches = cleanUpBranches(step.branches);
                        }
                    });
                    branch.steps = branch.steps.filter((step) => {
                        if (step.type === "branch" && step.branches.length === 0) return false;
                        return true;
                    });
                }
            });
            return branches.filter((branch) => branch.steps && branch.steps.length > 0);
        }

        if(branch === "root"){
            workflow.branches.splice(index, 1)
        }
        else{
            searchAndRemove(workflow.branches);
        }
        workflow.branches = cleanUpBranches(workflow.branches);
        workflow = assignUniqueBranchIds(assignUniqueStepIds(workflow));
        workflow.saved = false;
        setSelectedWorkflow(workflow);
    };

    // Save step modal: find and update the edited step in the workflow using recursion.
    const saveStepModal = (updatedStep = selectedStep) => {
        if (!selectedWorkflow) return;
        const newWorkflow = { ...selectedWorkflow };
        const found = findAndUpdateStep(newWorkflow.branches, updatedStep);
        if (found) {
            newWorkflow.saved = false;
            setSelectedWorkflow(newWorkflow);
        }
        setSelectedStep(null);
    };

    const openBranchConditions = (branch) => {
        const precondition = branch.precondition || {};
        setSelectedBranch(branch);
        setBranchConditionsDraft({
            condition_logic: precondition.condition_logic || "all",
            conditions: precondition.conditions || [],
        });
    };

    const closeBranchConditions = () => {
        setSelectedBranch(null);
        setBranchConditionsDraft(null);
    };

    const saveBranchConditions = () => {
        if (!selectedWorkflow || !selectedBranch || !branchConditionsDraft) return closeBranchConditions();
        const newWorkflow = { ...selectedWorkflow };
        const target = findBranchById(newWorkflow.branches, selectedBranch.id);
        if (target) {
            const logic = branchConditionsDraft.condition_logic || "all";
            const conditions = branchConditionsDraft.conditions || [];
            // An empty list and an absent key mean the same thing to the API, and
            // "always" evaluates to true there, so neither gates anything: drop the
            // precondition rather than leave a gate that never blocks.
            if (conditions.length > 0 && logic !== "always") {
                target.precondition = { condition_logic: logic, conditions };
            } else {
                delete target.precondition;
            }
            newWorkflow.saved = false;
            setSelectedWorkflow(newWorkflow);
        }
        closeBranchConditions();
    };

    // Recursively search for a branch (by id), including nested branches.
    const findBranchById = (branches, branchId) => {
        if (!branches) return null;
        for (const branch of branches) {
            if (branch.id === branchId) return branch;
            for (const step of branch.steps || []) {
                if (step.branches && step.branches.length > 0) {
                    const found = findBranchById(step.branches, branchId);
                    if (found) return found;
                }
            }
        }
        return null;
    };

    // Recursively search for a step (by id) and update it.
    const findAndUpdateStep = (branches, updatedStep) => {
        if (!branches) return false;
        for (let b = 0; b < branches.length; b++) {
            const branch = branches[b];
            if (!branch.steps) continue;
            for (let s = 0; s < branch.steps.length; s++) {
                let step = branch.steps[s];
                if (step.id === selectedStep.id) {
                    branch.steps[s] = updatedStep;
                    return true;
                }
                if (step.branches && step.branches.length > 0) {
                    if (findAndUpdateStep(step.branches, updatedStep)) return true;
                }
            }
        }
        return false;
    };

    // Append a new step to a given branch at a specified index
    const appendStepToBranch = (branch, step_index) => {
        // Clone workflow deeply to avoid mutation
        let workflow = JSON.parse(JSON.stringify(selectedWorkflow));
        let targetBranchId = branch.id;
        // Template for a new step
        const newStepTemplate = {
            "name":"",
            "active": true,
            type: "step",
            condition_logic:"all",
            conditions: [
                {id: 1, type: "amount_greater", operator: "contain", value: 0},
            ],
            actions: [
                { id: 1, type: "send_email", value: "" },
            ],
        };

        function searchAndAppend(branches) {
            if (!branches) return false;
            for (let branch of branches) {
                if (branch.id === targetBranchId) {
                    branch.steps = branch.steps || [];
                    branch.steps.splice(step_index, 0, { ...newStepTemplate, rank: step_index + 1 });
                    return true;
                }
                if (branch.steps && branch.steps.length > 0) {
                    for (let step of branch.steps) {
                        if (step.branches && step.branches.length > 0) {
                            if (searchAndAppend(step.branches)) return true;
                        }
                    }
                }
            }
            return false;
        }

        searchAndAppend(workflow.branches);
        workflow = assignUniqueBranchIds(assignUniqueStepIds(workflow));
        setSelectedWorkflow(workflow);
    };

    // Append a new branch to a branch
    const appendBranchToBranch = (branch, branchType = "children") => {
        let workflow = { ...selectedWorkflow };
        let targetBranchId = branch.id;
        const newBranchTemplate = {
            type: "branch",
            branches: [
                {
                    steps: [
                        {
                            "name":"",
                            "active": true,
                            type: "step",
                            condition_logic:"all",
                            conditions: [
                                {id: 1, type: "amount_greater", operator: "contain", value: 0},
                            ],
                            actions: [
                                { id: 1, type: "send_email", value: "" },
                            ],
                        },
                    ],
                },
                {
                    steps: [
                        {
                            "name":"",
                            "active": true,
                            type: "step",
                            condition_logic:"all",
                            conditions: [
                                {id: 1, type: "amount_greater", operator: "contain", value: 0},
                            ],
                            actions: [
                                { id: 1, type: "send_email", value: "" },
                            ],
                        },
                    ],
                },
            ],
        };

        if (targetBranchId === 0) {
            workflow.branches.push({ ...newBranchTemplate.branches[0] });
            workflow = assignUniqueBranchIds(assignUniqueStepIds(workflow));
            setSelectedWorkflow(workflow);
            centerContainer();
            return;
        }

        function searchAndAppend(branches) {
            if (!branches) return false;
            for (let branch of branches) {
                if (branch.id === targetBranchId) {
                    if (branchType === "parallel") {
                        branch.steps.forEach((step) => {
                            if (step.type === "branch") {
                                step.branches?.push({ ...newBranchTemplate.branches[0] });
                            }
                        });
                    } else {
                        branch.steps.push({ ...newBranchTemplate });
                    }
                    return true;
                }
                if (branch.steps && branch.steps.length > 0) {
                    for (let step of branch.steps) {
                        if (step.branches && step.branches.length > 0) {
                            if (searchAndAppend(step.branches)) return true;
                        }
                    }
                }
            }
            return false;
        }

        searchAndAppend(workflow.branches);
        workflow = assignUniqueBranchIds(assignUniqueStepIds(workflow));
        setSelectedWorkflow(workflow);
    };

    // Cancel edits: if unsaved, show modal; otherwise, reset selection and refetch.
    const cancelEdits = () => {
        if (selectedWorkflow && selectedWorkflow.saved === false) {
            setShowDiscardChangesModal(true);
        } else {
            setSelectedWorkflow(null);
            setSelectedStep(null);
            fetchWorkflows();
        }
    };

    // Reordering steps for drag-and-drop
    const reorderList = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const handleStepDragEnd = (result) => {
        if (!result.destination) return;
        if (
            result.source.index === result.destination.index &&
            result.source.droppableId === result.destination.droppableId
        )
            return;
        let updatedWorkflow = { ...selectedWorkflow };
        updatedWorkflow.steps = reorderList(
            updatedWorkflow.steps,
            result.source.index,
            result.destination.index
        );
        updatedWorkflow.steps.forEach((step, index) => {
            step.rank = index + 1;
        });
        updatedWorkflow.saved = false;
        setSelectedWorkflow(updatedWorkflow);
    };

    // Pagination handlers
    const handlePageChange = (newPage) => setPage(newPage);
    const handleRowsPerPageChange = (rows) => {
        setPage(1);
        setPageSize(rows);
    };

    // Derived pagination values
    const indexOfFirstItem = (page - 1) * pageSize;
    const indexOfLastItem = indexOfFirstItem + workflows.length - 1;
    const totalPages = Math.ceil(totalRowCount / pageSize);
    const emptyRowsCount = Math.max(0, pageSize - workflows.length);
    const emptyRows = Array(emptyRowsCount).fill({});

    return (
        <div className="card rounded-lg pb-3">
            {/* If workflows exist, render the main view */}
            {workflows ? (
                <div>
                    {/* LIST VIEW: When no workflow is selected, show the table view */}
                    {!selectedWorkflow && (
                        <div>
                            <div className="workflow-page pl-4 pr-4">
                                <div className="d-flex pt-4 pl-4 pr-4 pb-4 align-items-center">
                                    <h2>Workflows</h2>
                                </div>
                                <div className="pagination-row">
                                    <FilterInputField
                                        filterValue={filterValue}
                                        setFilterValue={setFilterValue}
                                        placeholder={""}
                                    />
                                    <PaginationControls
                                        page={page}
                                        totalPages={totalPages}
                                        indexOfFirstItem={indexOfFirstItem}
                                        indexOfLastItem={indexOfLastItem}
                                        handlePageChange={handlePageChange}
                                        totalItems={totalRowCount}
                                        data={data}
                                        handleRowsPerPageChange={handleRowsPerPageChange}
                                        rowsPerPage={pageSize}
                                    />
                                </div>
                                <div className="d-flex justify-content-end align-items-center py-2">
                                    <div className="d-flex">
                                        <button
                                            disabled={workflowsDeleteSelection.length === 0}
                                            onClick={() => setShowDeleteWorkflowsModal(true)}
                                            type="button"
                                            className="btn btn-secondary btn-sm ml-2"
                                        >
                                            {data.t.read("delete") + " (" + workflowsDeleteSelection.length + ")"}
                                        </button>
                                        <button onClick={() => newWorkflow()} type="button" className="btn btn-primary btn-sm ml-2">
                                            {data.t.read("add")}
                                        </button>
                                    </div>
                                </div>
                                <div className="mb-2 card rounded-lg admin-table">
                                    <table className="table table-hover">
                                        <thead>
                                        <tr>
                                            <th>
                                                <Checkbox
                                                    disabled={!workflows || !workflows.length}
                                                    isChecked={isAllWorkflowsSelected()}
                                                    onChange={toggleAllWorkflowsSelected}
                                                />
                                            </th>
                                            <th className="col-4">{data.t.read("name")}</th>
                                            <th className="col-6">Description</th>
                                            <th className="text-center col-2">{data.t.read("active")}</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {workflows.map((workflow, i) => (
                                            <tr key={workflow._id?.$oid || i}>
                                                <td>
                                                    <Checkbox
                                                        isChecked={isWorkflowSelected(workflow)}
                                                        onChange={() =>
                                                            isWorkflowSelected(workflow)
                                                                ? setWorkflowsDeleteSelection(
                                                                    workflowsDeleteSelection.filter((x) => x !== workflow._id?.$oid)
                                                                )
                                                                : setWorkflowsDeleteSelection([...workflowsDeleteSelection, workflow._id?.$oid])
                                                        }
                                                    />
                                                </td>
                                                <td className="col-4" onClick={() => openWorkflow(workflow)}>
                                                    {workflow.name}
                                                    {staleCategoryWarning(workflow)}
                                                </td>
                                                <td className="col-6" onClick={() => openWorkflow(workflow)}>
                                                    {workflow.description}
                                                </td>
                                                <td className="col-2">
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <Switch
                                                            checked={workflow.active}
                                                            width={70}
                                                            onColor="#F39800"
                                                            onHandleColor="#2693e6"
                                                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                            onChange={(active) => {
                                                                updateWorkflow({...workflow, active: active});
                                                            }}
                                                        />
                                                    </div>
                                                </td>
                                                <td onClick={() => openWorkflow(workflow)}>
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <FontAwesomeIcon className="mr-2" icon={faPencilAlt} type="button" />
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        {emptyRows.map((_, idx) => (
                                            <tr key={`empty-${idx}`}>
                                                <td style={{ height: "50px" }} colSpan="7">
                                                    &nbsp;
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* DETAIL VIEW: Render selected workflow details */}
                    {selectedWorkflow && (
                        <div>
                            <div className="workflow-page workflow-container">
                                <div className="d-flex p-4 align-items-center justify-content-between" style={{ background: "white" }}>
                                    <div className="d-flex ml-4 align-items-center">
                                        <div>
                                            <FontAwesomeIcon
                                                className="mr-3"
                                                icon={faChevronCircleLeft}
                                                type="button"
                                                style={{ fontSize: "18px" }}
                                                onClick={() => cancelEdits()}
                                            />
                                        </div>
                                        <h2>{selectedWorkflow.name}</h2>
                                        <div className="d-flex align-items-center">
                                            <button type="submit" className="btn btn-secondary mx-4" onClick={() => setShowWorkflowDetails(true)}>
                                                {data.t.read("details")}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="d-flex mx-2 justify-content-between align-items-end">
                                        <button type="button" className="btn btn-secondary" onClick={()=>{
                                            setWorkflowsDeleteSelection([selectedWorkflow._id?.$oid])
                                            setShowDeleteWorkflowsModal(true)                                        }
                                        }>
                                            {data.t.read("rule.delete_rule")}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={selectedWorkflow.saved === true || saving}
                                            className="btn btn-primary mx-3"
                                            onClick={handleSave}
                                        >
                                            {data.t.read("rule.save_rule")}
                                        </button>
                                    </div>
                                </div>
                                {/* Container for branches and steps */}
                                <div className="" style={{ height: "81vh", overflow: "auto" }} ref={containerRef}>
                                    <div className="h-100" style={{ minWidth: "100%", width: "fit-content" }}>
                                        <div className="mx-4 my-0 pt-4 pb-0 border-radius-30">
                                            {selectedWorkflow.trigger && (
                                                <>
                                                    <WorkflowTriggerEdit data={data} updateTrigger={updateTrigger} selectedTrigger={selectedWorkflow?.trigger} trigger_configs={TRIGGER_CONFIGS} default_trigger_type={DEFAULT_TRIGGER_TYPE}/>
                                                </>
                                            )}
                                            {/* Button to add a new branch */}
                                            <div className="d-flex justify-content-center">
                                                <div
                                                    className="card shadow-sm border-radius-30 btn-light"
                                                    type="button"
                                                    onClick={() => appendBranchToBranch({ id: 0 })}
                                                >
                                                    <div className="card-body" ref={branchButtonRef}>
                                                        <div className="d-flex justify-content-center align-items-center">
                                                            <FontAwesomeIcon icon={faPlus} />
                                                            <span className="ml-2">{data.t.read("branch")}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="step-connector" />
                                            <div className="d-flex justify-content-center border-radius-30 h-100">
                                                {selectedWorkflow.branches.map((branch, index) => (
                                                    <div className="d-flex w-100 justify-content-center" key={index}>
                                                        <WorkflowBranchEdit
                                                            branch={branch}
                                                            data={data}
                                                            expenseRates={expenseRates}
                                                            clientImputations={clientImputations}
                                                            onStepClicked={setSelectedStep}
                                                            onBranchConditionsClicked={openBranchConditions}
                                                            addStep={appendStepToBranch}
                                                            addBranch={appendBranchToBranch}
                                                            deleteStep={removeStepFromBranch}
                                                            deleteBranch={removeBranch}
                                                            branchPosition={
                                                                selectedWorkflow.branches.length === 1
                                                                    ? "single"
                                                                    : index === 0
                                                                        ? "start"
                                                                        : index === selectedWorkflow.branches.length - 1
                                                                            ? "end"
                                                                            : "middle"
                                                            }
                                                            branch_index={index}
                                                            branchToDelete={"root"}
                                                            validateAction={validateAction} validateCondition={validateCondition} validateStep={validateStep}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Step editing modal */}
                    {selectedStep != null && (
                        <Modal title={"Edit Step"} width={"920px"}>
                            <WorkflowEditStepModal
                                selectedStep={selectedStep}
                                data={data}
                                setSelectedStep={setSelectedStep}
                                onSubmit={() => saveStepModal()}
                                onCancel={() => setSelectedStep(null)}
                            />
                        </Modal>
                    )}
                    {/* Branch precondition modal */}
                    {selectedBranch != null && branchConditionsDraft != null && (
                        <Modal title={data.t.read("rule.branch_precondition")} width={"920px"}>
                            <WorkflowEditStepModal
                                conditionsOnly
                                selectedStep={branchConditionsDraft}
                                data={data}
                                setSelectedStep={setBranchConditionsDraft}
                                onSubmit={() => saveBranchConditions()}
                                onCancel={() => closeBranchConditions()}
                            />
                        </Modal>
                    )}
                    {/* Workflow details modal */}
                    {showWorkflowDetails && (
                        <Modal
                            title={"Workflow Details"}
                            width={"600px"}
                            okText={"OK"}
                            okAction={() => setShowWorkflowDetails(false)}
                            cancelText={"Cancel"}
                            cancelAction={() => setShowWorkflowDetails(false)}
                        >
                            <div className="card-body">
                                <div className="row mb-4">
                                    <label className="col-3">{data.t.read("rule.name")}:</label>
                                    <div className="col-8">
                                        <input
                                            type="text"
                                            onFocus={(e) => e.target.select()}
                                            required
                                            className="form-control"
                                            placeholder={data.t.read("rule.name")}
                                            value={selectedWorkflow.name}
                                            onChange={(e) =>
                                                setSelectedWorkflow({
                                                    ...selectedWorkflow,
                                                    name: e.target.value,
                                                    saved: false,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <label className="col-3">{data.t.read("rule.status")}:</label>
                                    <div className="col-8">
                                        <Switch
                                            className="switch mt-0"
                                            checked={selectedWorkflow.active}
                                            width={70}
                                            onColor="#F39800"
                                            onHandleColor="#2693e6"
                                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                            onChange={(active) =>
                                                setSelectedWorkflow({
                                                    ...selectedWorkflow,
                                                    active: active,
                                                    saved: false,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="row mb-4">
                                    <label className="col-3">{data.t.read("rule.priority")}:</label>
                                    <div className="col-8">
                                        <input
                                            type="number"
                                            onFocus={(e) => e.target.select()}
                                            required
                                            className="form-control"
                                            placeholder={data.t.read("rule.name")}
                                            value={selectedWorkflow.priority ?? 1}
                                            onChange={(e) =>
                                                setSelectedWorkflow({
                                                    ...selectedWorkflow,
                                                    priority: parseInt(e.target.value),
                                                    saved: false,
                                                })
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="row mb-2">
                                    <label className="col-3">Description:</label>
                                    <div className="col-8">
                                        <textarea required className="form-control" placeholder="Description"
                                                  value={selectedWorkflow.description}
                                                  onChange={(e) => setSelectedWorkflow({
                                                      ...selectedWorkflow,
                                                      description: e.target.value,
                                                      saved: false,
                                                  })}/>
                                    </div>
                                </div>
                            </div>
                        </Modal>
                    )}
                    {/* Error Modal */}
                    {errorMessage && (
                        <Modal title={data.t.read("attention")} okText={"OK"} okAction={() => setErrorMessage(null)}>
                            <p style={{whiteSpace: "pre-line"}} className="text-danger">
                                {errorMessage}
                            </p>
                        </Modal>
                    )}
                    {/* Discard changes Modal */}
                    {showDiscardChangesModal && (
                        <Modal
                            title={data.t.read("rule.discard_changes")}
                            okAction={() => {
                                setSelectedWorkflow(null);
                                fetchWorkflows();
                                setShowDiscardChangesModal(false);
                            }}
                            cancelAction={() => setShowDiscardChangesModal(false)}
                            okText={"OK"}
                            cancelText={data.t.read("cancel")}
                        >
                            <p className="text-danger">{data.t.read("rule.unsaved_changes_warning")}</p>
                        </Modal>
                    )}
                    {/* Delete workflows Modal */}
                    {showDeleteWorkflowsModal && (
                        <Modal
                            title={data.t.read("office_deletion")}
                            okAction={deleteWorkflows}
                            cancelAction={cancelDeleteWorkflowModal}
                            okText={data.t.read("delete")}
                            cancelText={data.t.read("cancel")}
                        >
                            <p className="text-danger">{data.t.read("delete")}?</p>
                        </Modal>
                    )}
                </div>
            ) : null}
        </div>
    );
};

export default inject("data")(observer(Workflow));
